import { useState } from 'react'
import { Plus, Trash2, Calculator, Users, Truck, Box, X, AlertTriangle, Search, Layers } from 'lucide-react'
import { CostoDiretto, CostoDirettoCreate, CostoMagazzinoCreate, CostiCalcolati, TipoCosto } from '../../types/offerte'
import { Articolo } from '../../types/magazzino'
import api from '../../core/services/api'
import { RUOLI_PERSONALE } from '../../types/personale'
import { TIPOLOGIE_STRUMENTO } from '../../types/strumenti'
import { BomAnalisiModal } from './BomAnalisiModal'

// Mappatura unita per tipologia mezzo
const UNITA_MEZZI: Record<string, string> = {
  'Auto': 'km', 'Furgone': 'km', 'Camion': 'km',
  'Strumento di lavoro': 'giorno', 'Macchinario': 'giorno',
}

interface CostiSectionProps {
  costiDiretti: CostoDiretto[]
  costiCalcolati: CostiCalcolati
  onAddCosto: (data: CostoDirettoCreate) => void
  onAddCostoMagazzino: (data: CostoMagazzinoCreate) => Promise<any>
  onDeleteCosto: (costoId: string) => void
  disabled?: boolean
  integrativaLabels?: Record<string, string>
  offertaId?: string
  totaleValore?: number
}

export function CostiSection({
  costiDiretti,
  costiCalcolati,
  onAddCosto,
  onAddCostoMagazzino,
  onDeleteCosto,
  disabled,
  integrativaLabels = {},
  offertaId,
  totaleValore = 0,
}: CostiSectionProps) {
  const [showBomAnalisi, setShowBomAnalisi] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modalStep, setModalStep] = useState<'tipo' | 'form'>('tipo')
  const [selectedTipo, setSelectedTipo] = useState<TipoCosto>('diretto')

  // Form fields
  const [descrizione, setDescrizione] = useState('')
  const [importo, setImporto] = useState('')
  const [sottotipo, setSottotipo] = useState('')
  const [quantita, setQuantita] = useState('')

  // Magazzino form
  const [articoli, setArticoli] = useState<Articolo[]>([])
  const [articoliLoading, setArticoliLoading] = useState(false)
  const [selectedArticolo, setSelectedArticolo] = useState<Articolo | null>(null)
  const [magSearch, setMagSearch] = useState('')
  const [magFilterTipo, setMagFilterTipo] = useState('prodotto_finito')
  const [magQuantita, setMagQuantita] = useState('')
  const [magCostoUnitario, setMagCostoUnitario] = useState('')
  const [addVoceLibera, setAddVoceLibera] = useState(false)
  const [voceDescrizione, setVoceDescrizione] = useState('')
  const [vocePrezzoUnitario, setVocePrezzoUnitario] = useState('')
  const [voceQuantita, setVoceQuantita] = useState('')
  const [magWarning, setMagWarning] = useState('')
  const [magSubmitting, setMagSubmitting] = useState(false)

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  const resetModal = () => {
    setShowModal(false)
    setModalStep('tipo')
    setSelectedTipo('diretto')
    setDescrizione('')
    setImporto('')
    setSottotipo('')
    setQuantita('')
    // Magazzino reset
    setSelectedArticolo(null)
    setMagSearch('')
    setMagFilterTipo('prodotto_finito')
    setMagQuantita('')
    setMagCostoUnitario('')
    setAddVoceLibera(false)
    setVoceDescrizione('')
    setVocePrezzoUnitario('')
    setVoceQuantita('')
    setMagWarning('')
    setArticoli([])
  }

  const loadArticoli = async () => {
    try {
      setArticoliLoading(true)
      const params: any = { attivo: true }
      if (magFilterTipo && magFilterTipo !== 'tutti') params.tipo = magFilterTipo
      if (magSearch) params.search = magSearch
      const res = await api.get('/v1/magazzino/articoli', { params })
      setArticoli(res.data)
    } catch {
      setArticoli([])
    } finally {
      setArticoliLoading(false)
    }
  }

  const handleSelectTipo = (tipo: TipoCosto) => {
    setSelectedTipo(tipo)
    setModalStep('form')
    if (tipo === 'magazzino') {
      loadArticoli()
    }
  }

  const handleSelectArticolo = (art: Articolo) => {
    setSelectedArticolo(art)
    setMagCostoUnitario(String(art.prezzo_acquisto || 0))
    setVoceDescrizione(`${art.codice} - ${art.descrizione}`)
  }

  const handleSubmitMagazzino = async () => {
    if (!selectedArticolo || !magQuantita) return
    setMagSubmitting(true)
    try {
      const data: CostoMagazzinoCreate = {
        articolo_id: selectedArticolo.id,
        quantita: parseFloat(magQuantita),
        costo_unitario: magCostoUnitario ? parseFloat(magCostoUnitario) : undefined,
        aggiungi_voce_libera: addVoceLibera,
      }
      if (addVoceLibera) {
        data.voce_descrizione = voceDescrizione || undefined
        data.voce_prezzo_unitario = vocePrezzoUnitario ? parseFloat(vocePrezzoUnitario) : undefined
        data.voce_quantita = voceQuantita ? parseFloat(voceQuantita) : undefined
      }
      const result = await onAddCostoMagazzino(data)
      if (result?.warning_scorte) {
        setMagWarning(`Scorte insufficienti - giacenza disponibile: ${result.giacenza_disponibile}`)
      }
      resetModal()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Errore'
      setMagWarning(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setMagSubmitting(false)
    }
  }

  const handleSubmit = () => {
    if (selectedTipo === 'diretto') {
      if (!descrizione || !importo) return
      onAddCosto({ tipo_costo: 'diretto', descrizione, importo: parseFloat(importo) })
    } else {
      if (!sottotipo || !quantita) return
      onAddCosto({ tipo_costo: selectedTipo, sottotipo, quantita: parseFloat(quantita) })
    }
    resetModal()
  }

  // Filtra sottotipi gia usati
  const usedPersonale = costiDiretti.filter(c => c.tipo_costo === 'personale').map(c => c.sottotipo)
  const usedMezzi = costiDiretti.filter(c => c.tipo_costo === 'mezzo').map(c => c.sottotipo)

  // Raggruppa costi per tipo
  const costiPerTipo = {
    diretto: costiDiretti.filter(c => c.tipo_costo === 'diretto'),
    personale: costiDiretti.filter(c => c.tipo_costo === 'personale'),
    mezzo: costiDiretti.filter(c => c.tipo_costo === 'mezzo'),
    magazzino: costiDiretti.filter(c => c.tipo_costo === 'magazzino'),
  }

  const getQuantitaLabel = (sottotipo: string) => {
    const unita = UNITA_MEZZI[sottotipo]
    return unita === 'km' ? 'Km' : 'Giorni'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-red-500" />
          Costi
        </h3>
        <div className="flex items-center gap-3">
          {offertaId && costiDiretti.some(c => c.tipo_costo === 'magazzino') && (
            <button
              onClick={() => setShowBomAnalisi(true)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              title="Analisi BOM e simulazione What-If"
            >
              <Layers className="w-4 h-4" />
              Analisi BOM
            </button>
          )}
          {!disabled && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus className="w-4 h-4" />
              Aggiungi Costo
            </button>
          )}
        </div>
      </div>

      {/* Lista costi - Diretti */}
      {costiPerTipo.diretto.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Costi Diretti</h4>
          <div className="space-y-1">
            {costiPerTipo.diretto.map((costo) => (
              <div key={costo.id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${costo.integrativa_origin_id ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  {costo.integrativa_origin_id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold">{integrativaLabels[costo.integrativa_origin_id] || 'INT'}</span>
                  )}
                  <span className="text-sm text-gray-700">{costo.descrizione}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900 text-sm">{formatCurrency(costo.importo)}</span>
                  {!disabled && !costo.integrativa_origin_id && (
                    <button onClick={() => onDeleteCosto(costo.id)} className="text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="text-right text-xs text-gray-500 mt-1">
            Subtotale: <span className="font-medium text-gray-700">{formatCurrency(costiCalcolati.costi_diretti_totale)}</span>
          </div>
        </div>
      )}

      {/* Lista costi - Personale */}
      {costiPerTipo.personale.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> Personale
          </h4>
          <div className="space-y-1">
            {costiPerTipo.personale.map((costo) => (
              <div key={costo.id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${costo.integrativa_origin_id ? 'bg-orange-100 border border-orange-300' : 'bg-orange-50'}`}>
                <div className="flex items-center gap-2">
                  {costo.integrativa_origin_id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold">{integrativaLabels[costo.integrativa_origin_id] || 'INT'}</span>
                  )}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{costo.sottotipo}</span>
                  <span className="text-xs text-gray-500">{costo.quantita} gg × {formatCurrency(costo.costo_unitario || 0)}/gg</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-orange-700 text-sm">{formatCurrency(costo.importo)}</span>
                  {!disabled && !costo.integrativa_origin_id && (
                    <button onClick={() => onDeleteCosto(costo.id)} className="text-orange-300 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="text-right text-xs text-orange-500 mt-1">
            Subtotale: <span className="font-medium text-orange-700">{formatCurrency(costiCalcolati.costo_personale_totale)}</span>
          </div>
        </div>
      )}

      {/* Lista costi - Mezzi */}
      {costiPerTipo.mezzo.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-medium text-yellow-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Truck className="w-3.5 h-3.5" /> Mezzi / Strumenti
          </h4>
          <div className="space-y-1">
            {costiPerTipo.mezzo.map((costo) => {
              const unita = UNITA_MEZZI[costo.sottotipo || ''] || 'u'
              const unitaLabel = unita === 'km' ? 'km' : 'gg'
              return (
                <div key={costo.id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${costo.integrativa_origin_id ? 'bg-orange-50 border border-orange-200' : 'bg-yellow-50'}`}>
                  <div className="flex items-center gap-2">
                    {costo.integrativa_origin_id && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold">{integrativaLabels[costo.integrativa_origin_id] || 'INT'}</span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">{costo.sottotipo}</span>
                    <span className="text-xs text-gray-500">{costo.quantita} {unitaLabel} × {formatCurrency(costo.costo_unitario || 0)}/{unitaLabel}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-yellow-700 text-sm">{formatCurrency(costo.importo)}</span>
                    {!disabled && !costo.integrativa_origin_id && (
                      <button onClick={() => onDeleteCosto(costo.id)} className="text-yellow-300 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="text-right text-xs text-yellow-500 mt-1">
            Subtotale: <span className="font-medium text-yellow-700">{formatCurrency(costiCalcolati.costo_mezzi_totale)}</span>
          </div>
        </div>
      )}

      {/* Lista costi - Magazzino */}
      {costiPerTipo.magazzino.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-medium text-teal-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Box className="w-3.5 h-3.5" /> Materiali da Magazzino
          </h4>
          <div className="space-y-1">
            {costiPerTipo.magazzino.map((costo) => (
              <div key={costo.id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${costo.integrativa_origin_id ? 'bg-orange-50 border border-orange-200' : 'bg-teal-50'}`}>
                <div className="flex items-center gap-2">
                  {costo.integrativa_origin_id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold">{integrativaLabels[costo.integrativa_origin_id] || 'INT'}</span>
                  )}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-mono">{costo.sottotipo}</span>
                  <span className="text-xs text-gray-500">{costo.quantita} pz × {formatCurrency(costo.costo_unitario || 0)}/pz</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-teal-700 text-sm">{formatCurrency(costo.importo)}</span>
                  {!disabled && !costo.integrativa_origin_id && (
                    <button onClick={() => onDeleteCosto(costo.id)} className="text-teal-300 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="text-right text-xs text-teal-500 mt-1">
            Subtotale: <span className="font-medium text-teal-700">{formatCurrency(costiCalcolati.costo_magazzino_totale)}</span>
          </div>
        </div>
      )}

      {/* Messaggio vuoto */}
      {costiDiretti.length === 0 && (
        <p className="text-sm text-gray-400 py-3">Nessun costo definito</p>
      )}

      {/* Totale Costi */}
      <div className="border-t border-gray-200 pt-4 mt-3">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-900">TOTALE COSTI</span>
          <span className="text-xl font-bold text-red-600">{formatCurrency(costiCalcolati.totale_costi)}</span>
        </div>
      </div>

      {/* Modale Aggiungi Costo */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-2xl w-full ${selectedTipo === 'magazzino' ? 'max-w-2xl' : 'max-w-md'} p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">
                {modalStep === 'tipo' ? 'Tipo di Costo' : (
                  selectedTipo === 'diretto' ? 'Costo Diretto' :
                  selectedTipo === 'personale' ? 'Costo Personale' :
                  selectedTipo === 'magazzino' ? 'Articolo da Magazzino' : 'Costo Mezzo'
                )}
              </h4>
              <button onClick={resetModal} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {modalStep === 'tipo' && (
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => handleSelectTipo('diretto')}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <Calculator className="w-8 h-8 text-gray-500" />
                  <span className="text-sm font-medium">Diretto</span>
                </button>
                <button
                  onClick={() => handleSelectTipo('personale')}
                  disabled={usedPersonale.length >= RUOLI_PERSONALE.length}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Users className="w-8 h-8 text-orange-500" />
                  <span className="text-sm font-medium">Personale</span>
                </button>
                <button
                  onClick={() => handleSelectTipo('mezzo')}
                  disabled={usedMezzi.length >= TIPOLOGIE_STRUMENTO.length}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-yellow-500 hover:bg-yellow-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Truck className="w-8 h-8 text-yellow-500" />
                  <span className="text-sm font-medium">Mezzo</span>
                </button>
                <button
                  onClick={() => handleSelectTipo('magazzino')}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-colors"
                >
                  <Box className="w-8 h-8 text-teal-500" />
                  <span className="text-sm font-medium">Magazzino</span>
                </button>
              </div>
            )}

            {modalStep === 'form' && selectedTipo === 'diretto' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
                  <input
                    type="text"
                    value={descrizione}
                    onChange={(e) => setDescrizione(e.target.value)}
                    placeholder="Es. Materiale di consumo"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Importo (EUR) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={importo}
                    onChange={(e) => setImporto(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setModalStep('tipo')} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Indietro</button>
                  <button
                    onClick={handleSubmit}
                    disabled={!descrizione || !importo}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >Aggiungi</button>
                </div>
              </div>
            )}

            {modalStep === 'form' && selectedTipo === 'personale' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo *</label>
                  <select
                    value={sottotipo}
                    onChange={(e) => setSottotipo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  >
                    <option value="">Seleziona ruolo...</option>
                    {RUOLI_PERSONALE.map(ruolo => (
                      <option key={ruolo} value={ruolo} disabled={usedPersonale.includes(ruolo)}>
                        {ruolo} {usedPersonale.includes(ruolo) ? '(gia inserito)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Giorni *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={quantita}
                    onChange={(e) => setQuantita(e.target.value)}
                    placeholder="Numero giorni"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setModalStep('tipo')} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Indietro</button>
                  <button
                    onClick={handleSubmit}
                    disabled={!sottotipo || !quantita}
                    className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  >Aggiungi</button>
                </div>
              </div>
            )}

            {modalStep === 'form' && selectedTipo === 'mezzo' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipologia *</label>
                  <select
                    value={sottotipo}
                    onChange={(e) => setSottotipo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                  >
                    <option value="">Seleziona tipologia...</option>
                    {TIPOLOGIE_STRUMENTO.map(tip => (
                      <option key={tip} value={tip} disabled={usedMezzi.includes(tip)}>
                        {tip} {usedMezzi.includes(tip) ? '(gia inserita)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {sottotipo ? getQuantitaLabel(sottotipo) : 'Quantita'} *
                  </label>
                  <input
                    type="number"
                    step={UNITA_MEZZI[sottotipo] === 'km' ? '1' : '0.5'}
                    min="0.5"
                    value={quantita}
                    onChange={(e) => setQuantita(e.target.value)}
                    placeholder={sottotipo ? `Numero ${getQuantitaLabel(sottotipo).toLowerCase()}` : 'Quantita'}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setModalStep('tipo')} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Indietro</button>
                  <button
                    onClick={handleSubmit}
                    disabled={!sottotipo || !quantita}
                    className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                  >Aggiungi</button>
                </div>
              </div>
            )}

            {modalStep === 'form' && selectedTipo === 'magazzino' && (
              <div className="space-y-3">
                {/* Filtri */}
                <div className="flex gap-2">
                  <div className="flex gap-1">
                    {['prodotto_finito', 'semilavorato', 'materia_prima', 'tutti'].map(t => (
                      <button
                        key={t}
                        onClick={() => { setMagFilterTipo(t); setTimeout(loadArticoli, 0) }}
                        className={`px-2 py-1 text-xs rounded-lg ${magFilterTipo === t ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {t === 'tutti' ? 'Tutti' : t === 'prodotto_finito' ? 'Prodotti' : t === 'semilavorato' ? 'Semilavorati' : 'Materie Prime'}
                      </button>
                    ))}
                  </div>
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cerca articolo..."
                      value={magSearch}
                      onChange={(e) => { setMagSearch(e.target.value); setTimeout(loadArticoli, 300) }}
                      className="w-full pl-7 pr-3 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Lista articoli */}
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {articoliLoading ? (
                    <div className="p-3 text-center text-xs text-gray-400">Caricamento...</div>
                  ) : articoli.length === 0 ? (
                    <div className="p-3 text-center text-xs text-gray-400">Nessun articolo trovato</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium text-gray-500">Codice</th>
                          <th className="px-2 py-1 text-left font-medium text-gray-500">Descrizione</th>
                          <th className="px-2 py-1 text-right font-medium text-gray-500">Giacenza</th>
                          <th className="px-2 py-1 text-right font-medium text-gray-500">Prezzo Acq.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articoli.map(art => (
                          <tr
                            key={art.id}
                            onClick={() => handleSelectArticolo(art)}
                            className={`cursor-pointer hover:bg-teal-50 ${
                              selectedArticolo?.id === art.id ? 'bg-teal-100' : ''
                            } ${art.giacenza_attuale <= 0 ? 'text-red-500' : ''}`}
                          >
                            <td className="px-2 py-1.5 font-mono">{art.codice}</td>
                            <td className="px-2 py-1.5 truncate max-w-[200px]">{art.descrizione}</td>
                            <td className="px-2 py-1.5 text-right font-medium">{art.giacenza_attuale} {art.unita_misura}</td>
                            <td className="px-2 py-1.5 text-right">{formatCurrency(art.prezzo_acquisto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Campi dopo selezione */}
                {selectedArticolo && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantita' *</label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          value={magQuantita}
                          onChange={(e) => setMagQuantita(e.target.value)}
                          placeholder="0"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Costo unitario</label>
                        <input
                          type="number"
                          step="0.01"
                          value={magCostoUnitario}
                          onChange={(e) => setMagCostoUnitario(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Totale costo</label>
                        <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700">
                          {formatCurrency((parseFloat(magQuantita) || 0) * (parseFloat(magCostoUnitario) || 0))}
                        </div>
                      </div>
                    </div>

                    {/* Warning scorte */}
                    {magQuantita && parseFloat(magQuantita) > selectedArticolo.giacenza_attuale && (
                      <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <span className="text-xs text-amber-700">
                          Scorte insufficienti — mancano {(parseFloat(magQuantita) - selectedArticolo.giacenza_attuale).toFixed(0)} {selectedArticolo.unita_misura}. L'offerta richiedera' autorizzazione per l'invio.
                        </span>
                      </div>
                    )}

                    {/* Toggle voce libera */}
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addVoceLibera}
                          onChange={(e) => {
                            setAddVoceLibera(e.target.checked)
                            if (e.target.checked) {
                              setVoceQuantita(magQuantita)
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                      </label>
                      <span className="text-xs text-gray-600">Aggiungi anche Voce Libera (prezzo vendita)</span>
                    </div>

                    {/* Sezione Voce Libera */}
                    {addVoceLibera && (
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-200 space-y-2">
                        <p className="text-xs font-medium text-teal-700">Voce Libera</p>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Descrizione</label>
                          <input
                            type="text"
                            value={voceDescrizione}
                            onChange={(e) => setVoceDescrizione(e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Quantita'</label>
                            <input
                              type="number"
                              value={voceQuantita}
                              onChange={(e) => setVoceQuantita(e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Prezzo unitario *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={vocePrezzoUnitario}
                              onChange={(e) => setVocePrezzoUnitario(e.target.value)}
                              placeholder="0.00"
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Totale vendita</label>
                            <div className="px-2 py-1.5 text-xs bg-white rounded-lg font-medium">
                              {formatCurrency((parseFloat(voceQuantita) || 0) * (parseFloat(vocePrezzoUnitario) || 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Warning */}
                    {magWarning && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{magWarning}</div>
                    )}
                  </>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setModalStep('tipo')} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Indietro</button>
                  <button
                    onClick={handleSubmitMagazzino}
                    disabled={!selectedArticolo || !magQuantita || magSubmitting || (addVoceLibera && !vocePrezzoUnitario)}
                    className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {magSubmitting ? 'Salvataggio...' : 'Aggiungi'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BOM Analisi Modal */}
      {showBomAnalisi && offertaId && (
        <BomAnalisiModal
          offertaId={offertaId}
          totaleValore={totaleValore}
          totaleCosti={costiCalcolati.totale_costi}
          costiDiretti={costiDiretti}
          onClose={() => setShowBomAnalisi(false)}
        />
      )}
    </div>
  )
}
